
import { Component, Prop, Vue } from 'vue-property-decorator';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import { userRights } from '@/helpers/CmsShowHelper';
import { downloadTableAsExcel, ExportColumn } from '@/helpers/PrintHelper';

@Component({
    name: 'UserRightsTable',
})
export default class UserRightsTable extends Vue {
    @Prop({ type: Array, required: false, default: () => [] }) readonly userGroups!: any[];

    private search: string = '';
    private userGroupId: string | null = null;
    private userRights = userRights;
    private innerUserGroupIds: Record<string, string | null> = {};

    private get columns() {
        const basicColumns = [
            {
                title: this.$t('Name'),
                dataIndex: 'name',
                key: 'name',
                sorter: (a: any, b: any) => a.name.localeCompare(b.name),
            },
            {
                title: this.$t('Usergroup'),
                dataIndex: 'usergroup',
                key: 'usergroup',
                scopedSlots: { customRender: 'userGroup' },
            },
        ];

        if (!this.users.length) {
            return basicColumns;
        }

        for (const userRightItem of this.userRights) {
            basicColumns.push({
                title: this.$t(userRightItem.label),
                dataIndex: userRightItem.name,
                key: userRightItem.name,
                sorter: (a: any, b: any) => {
                    const statusA = a[userRightItem.name] || '';
                    const statusB = b[userRightItem.name] || '';
                    return statusA.localeCompare(statusB);
                },
            });
        }

        return basicColumns;
    }

    private get myData() {
        return this.users
            .map((user) => {
                if (this.search && !user?.name.toLowerCase().includes(this.search.toLowerCase())) {
                    return null;
                }

                if (
                    this.userGroupId &&
                    user.userGroupConnections.every((connection: any) => connection.userGroup?.id !== this.userGroupId)
                ) {
                    return null;
                }

                const userData: any = {
                    name: user.name,
                    usergroup: this.selectedUserGroup?.name || 'No group',
                    key: user.id,
                };

                for (const userRightItem of this.userRights) {
                    userData[userRightItem.name] = this.determineUserRightStatus(user, userRightItem);
                }

                return userData;
            })
            .filter((userData) => userData !== null);
    }

    private get users() {
        return CMSUserRepository.getAllExceptArchivedUsers();
    }

    private get currentUserGroup() {
        return String(this.$store.getters['jwtData/currentUserGroup']);
    }

    private get selectedUserGroup() {
        return UserGroupRepository.getById(this.userGroupId || this.currentUserGroup);
    }

    private onUserGroupFilterChange(value: string) {
        this.innerUserGroupIds = {}; // Reset inner user group IDs
        this.users.forEach((user: any) => {
            this.$set(this.innerUserGroupIds, user.id, value); // Initialize with selected user group
        });
    }

    private onInnerUGFilterChange(user: any, value: string) {
        this.$set(this.innerUserGroupIds, user.key, value);
    }

    private determineUserRightStatus(user: any, userRightItem: { name: string; label: string }) {
        const userGroupConnection = user.userGroupConnections.find(
            (connection: any) => connection.userGroup?.id === (this.innerUserGroupIds[user.id] || this.currentUserGroup)
        );

        if (!userGroupConnection) {
            return 'No connection';
        }

        if (userGroupConnection.userRights) {
            // If it's an object, check for property existence or value
            // add icons
            return userGroupConnection.userRights[userRightItem.name] ? '✅' : '❌';
        }

        return 'No';
    }

    private filteredUserGroups(record: any) {
        return this.userGroups.filter((userGroup) => {
            const userGroupConnection = this.users.find((user: any) => user.id === record)?.userGroupConnections;
            return userGroupConnection?.some((connection: any) => connection.userGroup?.id === userGroup.id);
        });
    }

    private downloadExcel() {
        const tableData = this.myData;
        const tableColumns = this.columns as ExportColumn[];

        if (!tableData || tableData.length === 0) {
            return;
        }

        downloadTableAsExcel(tableColumns, tableData, 'user-rights');
    }

    private async mounted() {
        try {
            this.userGroupId = this.currentUserGroup || null;

            this.users.forEach((user: any) => {
                this.$set(this.innerUserGroupIds, user.id, this.currentUserGroup || null); // Initialize with current user group
            });
        } catch (error) {
            console.error('Error', error);
        }
    }
}
